import { z } from 'zod';
import { NotificationMethodType } from './AlertBuilderSchemaTypes';

// Enums
export const FrequencySchema = z.enum(['daily', 'weekly', 'monthly', 'when conditions are met']);

export const NotificationMethodSchema = z.enum(['Email', 'Slack', 'Teams', 'Webhook']);

export const WebhookSecurityMethodSchema = z.enum(['urlKey', 'bearerToken']);

export const OperatorTypeSchema = z.enum(['AND', 'OR', 'greater than', 'less than']);

export const ThresholdUnitSchema = z.enum(['%', 'units', '$']);

export const LogicalOperatorsSchema = z.enum(['AND', 'OR']);

export const ComparisonOperatorsSchema = z.enum(['greater than', 'less than']);

export const SelectedProductSchema = z.enum(['atlas', 'beacon']);

export const MetricFilterOperatorSchema = z.enum([
  '>',
  '>=',
  '<',
  '<=',
  '=',
  'between',
  'ranked_in_top',
  'not_ranked_in_top'
]);

// Response Types Schema
export const ResponseTypesSchema = z.enum([
  'CategoryId',
  'SubcategoryId',
  'BrandId',
  'Searchterm',
  'WeekId',
  'RetailerId',
  'RetailerSku'
]);

export const AtlasGlobalFilterFieldTypeSchema = z.enum([
  'Category',
  'Retailer'
]);

export const BeaconGlobalFilterFieldTypeSchema = z.enum([
  'Brand',
  'Retailer'
]);

export const GlobalFilterFieldTypeSchema = z.union([
  AtlasGlobalFilterFieldTypeSchema,
  BeaconGlobalFilterFieldTypeSchema
]);

// Filter Field Types Schema
export const AtlasFilterFieldTypeSchema = z.enum([
  'Category',
  'Subcategory',
  'Brand',
  'Retailer',
  'Segment',
  'Business Unit',
  'RetailerSku',
  'Keyword'
]);

export const BeaconFilterFieldTypeSchema = z.enum([
  ...AtlasFilterFieldTypeSchema.options,
  'Campaign'
] as const);

export const FilterFieldConceptSchema = z.enum([
  'categoryid',
  'subcategoryid',
  'brandid',
  'retailerid',
  'retailersku',
  'searchterm',
  'businessunitid',
  'segmentid',
  'campaignid'
]);

export const FilterTypeToConceptMap = {
  [BeaconFilterFieldTypeSchema.enum['Category']]: FilterFieldConceptSchema.enum['categoryid'],
  [BeaconFilterFieldTypeSchema.enum['Subcategory']]: FilterFieldConceptSchema.enum['subcategoryid'],
  [BeaconFilterFieldTypeSchema.enum['Brand']]: FilterFieldConceptSchema.enum['brandid'],
  [BeaconFilterFieldTypeSchema.enum['Retailer']]: FilterFieldConceptSchema.enum['retailerid'],
  [BeaconFilterFieldTypeSchema.enum['RetailerSku']]: FilterFieldConceptSchema.enum['retailersku'],
  [BeaconFilterFieldTypeSchema.enum['Keyword']]: FilterFieldConceptSchema.enum['searchterm'],
  [BeaconFilterFieldTypeSchema.enum['Business Unit']]: FilterFieldConceptSchema.enum['businessunitid'],
  [BeaconFilterFieldTypeSchema.enum['Segment']]: FilterFieldConceptSchema.enum['segmentid'],
  [BeaconFilterFieldTypeSchema.enum['Campaign']]: FilterFieldConceptSchema.enum['campaignid']
} as const;

export const FilterFieldTypeSchema = z.union([
  AtlasFilterFieldTypeSchema,
  BeaconFilterFieldTypeSchema
]);


// Atlas Metrics Schema
export const AtlasMetricsSchema = z.enum([
  'Total Traffic',
  'Organic Traffic',
  'Paid Traffic',
  'Other Traffic',
  'Paid Ad Spend',
  'Conversion Rate',
  'Retail Price',
  'Retail Sales',
  'Units Sold'
]);

// Beacon Metrics Schema
export const BeaconMetricsSchema = z.enum([
  'Total Traffic',
  'Organic Traffic',
  'Paid Traffic',
  'Other Traffic',
  'Impressions',
  'Ad Spend',
  'Ad Sales',
  'Ad Units Sold',
  'CTR',
  'CPC',
  'CPA',
  'Conversion Rate',
  'ROAS',
  'Retail Price',
  'Wholesale Price',
  'In-stock Rate',
  'Units on Hand',
  'Weeks on Hand',
  'On Hand - Retail',
  'Retail Sales',
  'Wholesale Sales',
  'Units Sold',
  'Retailer Margin',
  'Retailer Margin %',
  'Units Returned',
  'Return Rate %',
  'Returns - Retail Value',
  'Unvariated Child Skus',
  'Variated Child Skus',
  'Accurate Child Skus',
  'Inaccurate Child Skus',
  'Child Sku Accuracy',
  'Child Variation Rate'
]);

export const MetricSchema = z.union([
  AtlasMetricsSchema,
  BeaconMetricsSchema
]);

// Keyword Sensible Metrics Schema - metrics that make sense when analyzing by keyword/searchterm
export const KeywordSensibleMetricsSchema = z.enum([
  'Organic Traffic',
  'Paid Traffic',
  'Impressions',
  'Ad Spend',
  'Ad Sales',
  'Ad Units Sold',
  'CTR',
  'CPC',
  'CPA',
  'ROAS'
]);

// Predefined Period Ranges Schema
export const PredefinedPeriodRangesSchema = z.enum([
  'Last Week',
  'Last 4 Weeks',
  'Last 13 Weeks',
  'Last 26 Weeks',
  'Last 52 Weeks',
  'YTD'
]);

export const PredefinedComparisonOptionsSchema = z.enum([
  'Prior Year',
  'Prior Period'
]);

export const ForTheFirstTimeOptionsSchema = z.enum([
  'Last Week'
]);

// Time Range Modes Schema
export const TimeRangeModesSchema = z.enum([
  'Predefined Period',
  'Predefined Comparison',
  'Custom Range',
  'Before Week',
  'After Week',
  'For the First Time'
]);

// Aggregations Schema
export const AggregationsSchema = z.enum([
  'none',
  'sum',
  'avg',
  'count',
  'median',
  'min',
  'max'
]);

// Filter Operators Schema
export const FilterOperatorsSchema = z.enum([
  '=',
  '!=',
  '>',
  '<',
  '>=',
  '<=',
  'LIKE',
  'NOT LIKE',
  'IN',
  'NOT IN',
  'BETWEEN',
  'IS NULL',
  'IS NOT NULL'
]);

// Metric Adjustments Schema
export const MetricAdjustmentsSchema = z.enum([
  'Divide by',
  'Multiply by',
  'Add',
  'Subtract'
]);

export const WeekReferencesSchema = z.enum([
  'beginning',
  'last_week',
  'year_start',
  'last_52w',
  'last_26w',
  'last_13w',
  'last_4w',
]);

export const WeekReferenceOperatorSchema = z.enum([
  '+',
  '-',
]);

export const MetricFilterConfigTypeSchema = z.object({
  operator: MetricFilterOperatorSchema,
  value: z.number().optional(),
  minValue: z.number().optional(),
  maxValue: z.number().optional(),
  rankLimit: z.number().optional(),
})
  .refine((data) => {
    // Validate metric filter based on operator
    if (data.operator === MetricFilterOperatorSchema.enum['between']) {
      return data.minValue !== undefined && data.maxValue !== undefined;
    }
    if (data.operator === MetricFilterOperatorSchema.enum['ranked_in_top'] || data.operator === MetricFilterOperatorSchema.enum['not_ranked_in_top']) {
      return data.rankLimit !== undefined && data.rankLimit > 0;
    }
    // For other operators, value should be present
    return data.value !== undefined;
  }, {
    message: "Invalid metric filter configuration: missing required values for operator",
  });

// Email Notification Sub-schema
export const EmailNotificationConfigSchema = z.object({
  recipients: z.array(z.string().email()),
});

// Slack Notification Sub-schema
export const SlackNotificationConfigSchema = z.object({
  methodId: z.string(),
  workspaceId: z.string(),
  workspaceName: z.string(),
  channel: z.string(),
  channelId: z.string(),
});

// Teams Notification Sub-schema
export const TeamsNotificationConfigSchema = z.object({
  methodId: z.string(),
  workspaceId: z.string(),
  workspaceName: z.string(),
  teamId: z.string(),
  teamName: z.string(),
  channel: z.string(),
  channelId: z.string(),
});

// Webhook Notification Sub-schema
export const WebhookNotificationConfigSchema = z.discriminatedUnion('security', [
  z.object({
    url: z.string().url(),
    security: z.literal('urlKey'),
  }),
  z.object({
    url: z.string().url(),
    security: z.literal('bearerToken'),
    bearerToken: z.string(),
  }),
]);

// Notification Config Schema
export const NotificationConfigSchema = z.object({
  methods: z.array(NotificationMethodSchema).optional().default([]),
  config: z.object({
    email: EmailNotificationConfigSchema.optional(),
    slack: SlackNotificationConfigSchema.optional(),
    teams: TeamsNotificationConfigSchema.optional(),
    webhook: WebhookNotificationConfigSchema.optional(),
  }),
})
  .refine((data) => {
    // At least one notification method must be configured
    const hasEmail = data.config.email?.recipients?.length && data.config.email.recipients.length > 0;
    const hasSlack = data.config.slack;
    const hasTeams = data.config.teams;
    const hasWebhook = data.config.webhook?.url;

    return hasEmail || hasSlack || hasTeams || hasWebhook;
  }, {
    message: "At least one notification method must be configured",
  })
  .transform((data) => {
    // Auto-populate methods array based on actual config
    const methods: NotificationMethodType[] = [];

    if (data.config.email?.recipients?.length && data.config.email.recipients.length > 0) {
      methods.push('Email');
    }
    if (data.config.slack) {
      methods.push('Slack');
    }
    if (data.config.teams) {
      methods.push('Teams');
    }
    if (data.config.webhook?.url) {
      methods.push('Webhook');
    }

    return {
      ...data,
      methods
    };
  });

// Alert Meta Schema
export const AlertMetaSchema = z.object({
  alertName: z.string().min(1, "Alert name is required"),
  alertDescription: z.string(),
});

// Placeholder for Create Alert Config Schema - will be defined after RuleJsonOutputSchema

// ===== SCHEMAS FROM JsonRuleSchema (for output types) =====

// Restricted operator schema for filters
export const FilterOperatorSchema = z.enum(['IN']);

// String-based filter item (for RetailerSku, Keyword, Business Unit, Segment, Campaign)
export const StringFilterItemSchema = z.object({
  id: z.string(),
  name: z.string(),
});

// Base Filter Schema - reusable pattern for all filter types
const createFilterSchema = <T extends keyof typeof BeaconFilterFieldTypeSchema.enum, C extends keyof typeof FilterFieldConceptSchema.enum>(
  filterType: T,
  concept: C,
  displayName: string
) => {
  return z.object({
    type: z.literal(BeaconFilterFieldTypeSchema.enum[filterType]),
    concept: z.literal(FilterFieldConceptSchema.enum[concept]),
    operator: z.literal(FilterOperatorSchema.enum['IN']),
    values: z.array(StringFilterItemSchema).min(1, `At least one ${displayName.toLowerCase()} must be selected for 'IN' operator`),
  });
};

// Reusable filter schemas using the base pattern
const CategoryFilterSchema = createFilterSchema('Category', 'categoryid', 'category');
const SubcategoryFilterSchema = createFilterSchema('Subcategory', 'subcategoryid', 'subcategory');
const BrandFilterSchema = createFilterSchema('Brand', 'brandid', 'brand');
const RetailerFilterSchema = createFilterSchema('Retailer', 'retailerid', 'retailer');
const RetailerSkuFilterSchema = createFilterSchema('RetailerSku', 'retailersku', 'retailer SKU');
const KeywordFilterSchema = createFilterSchema('Keyword', 'searchterm', 'keyword');
const BusinessUnitFilterSchema = createFilterSchema('Business Unit', 'businessunitid', 'business unit');
const SegmentFilterSchema = createFilterSchema('Segment', 'segmentid', 'segment');
const CampaignFilterSchema = createFilterSchema('Campaign', 'campaignid', 'campaign');

// Regular filters (all except Business Unit and Segment)
export const AtlasFilterSchema = z.union([
  CategoryFilterSchema,
  SubcategoryFilterSchema,
  BrandFilterSchema,
  RetailerFilterSchema,
  RetailerSkuFilterSchema,
  KeywordFilterSchema,
  BusinessUnitFilterSchema,
  SegmentFilterSchema,
]);

export const BeaconFilterSchema = z.union([
  AtlasFilterSchema,
  CampaignFilterSchema
]);

export const FilterConfigSchema = BeaconFilterSchema;

export const RegularFilterSchema = z.union([
  CategoryFilterSchema,
  SubcategoryFilterSchema,
  BrandFilterSchema,
  RetailerFilterSchema,
  RetailerSkuFilterSchema,
  KeywordFilterSchema,
  CampaignFilterSchema,
]);

export const FilterConfigArraySchema = z.union([
  z.array(RegularFilterSchema),
  z.array(BusinessUnitFilterSchema),
  z.array(SegmentFilterSchema),
]);

export const AtlasGlobalFilterTypeSchema = z.union([
  CategoryFilterSchema,
  RetailerFilterSchema,
]);

export const BeaconGlobalFilterTypeSchema = z.union([
  BrandFilterSchema,
  RetailerFilterSchema,
]);

export const GlobalFilterTypeSchema = z.union([
  AtlasGlobalFilterTypeSchema,
  BeaconGlobalFilterTypeSchema
]);

export const StartWeekConfigSchema = z.discriminatedUnion('useRelativeStartWeek', [
  z
    .object({
      useRelativeStartWeek: z.literal(true),
      relativeStartReference: WeekReferencesSchema,
      relativeStartOperator: WeekReferenceOperatorSchema,
      relativeStartOffset: z.number().min(0, "Offset must be greater or equal to 0"),
    })
    .passthrough(),
  z
    .object({
      useRelativeStartWeek: z.literal(false),
      startWeek: z.number(),
    })
    .passthrough(),
]);

export const EndWeekConfigSchema = z.discriminatedUnion('useRelativeEndWeek', [
  z
    .object({
      useRelativeEndWeek: z.literal(true),
      relativeEndReference: WeekReferencesSchema,
      relativeEndOperator: WeekReferenceOperatorSchema,
      relativeEndOffset: z.number().min(0, "Offset must be greater or equal to 0"),
    })
    .passthrough(),
  z
    .object({
      useRelativeEndWeek: z.literal(false),
      endWeek: z.number(),
    })
    .passthrough(),
]);

// Custom Range Schema (intersection of start and end week configs)
export const CustomRangeSchema = StartWeekConfigSchema.and(EndWeekConfigSchema);

// Before Week Schema - using discriminated union for type safety
export const BeforeWeekSchema = z.discriminatedUnion('useRelativeBeforeWeek', [
  z.object({
    useRelativeBeforeWeek: z.literal(true),
    relativeBeforeWeekReference: WeekReferencesSchema,
    relativeBeforeWeekOperator: WeekReferenceOperatorSchema,
    relativeBeforeWeekOffset: z.number(),
  }),
  z.object({
    useRelativeBeforeWeek: z.literal(false),
    beforeWeekValue: z.number(),
  }),
]);

// After Week Schema - using discriminated union for type safety
export const AfterWeekSchema = z.discriminatedUnion('useRelativeAfterWeek', [
  z.object({
    useRelativeAfterWeek: z.literal(true),
    relativeAfterWeekReference: WeekReferencesSchema,
    relativeAfterWeekOperator: WeekReferenceOperatorSchema,
    relativeAfterWeekOffset: z.number(),
  }),
  z.object({
    useRelativeAfterWeek: z.literal(false),
    afterWeekValue: z.number(),
  }),
]);

// Time Configuration Schema (for output) - using discriminated union for type safety
export const TimeConfigurationSchema = z.discriminatedUnion('mode', [
  z.object({
    mode: z.literal(TimeRangeModesSchema.enum['Predefined Period']),
    timeRange: PredefinedPeriodRangesSchema,
  }),
  z.object({
    mode: z.literal(TimeRangeModesSchema.enum['Custom Range']),
    customRange: CustomRangeSchema,
  }),
  z.object({
    mode: z.literal(TimeRangeModesSchema.enum['Before Week']),
    beforeWeek: BeforeWeekSchema,
  }),
  z.object({
    mode: z.literal(TimeRangeModesSchema.enum['After Week']),
    afterWeek: AfterWeekSchema,
  }),
  z.object({
    mode: z.literal(TimeRangeModesSchema.enum['Predefined Comparison']),
    timeRange: PredefinedPeriodRangesSchema,
    comparisonOption: PredefinedComparisonOptionsSchema,
  }),
  z.object({
    mode: z.literal(TimeRangeModesSchema.enum['For the First Time']),
    forTheFirstTimeOption: ForTheFirstTimeOptionsSchema,
  }),
]);

// Metric Adjustment Schema (for output) - using discriminated union for type safety
export const MetricAdjustmentSchema = z.discriminatedUnion('useReferenceFilter', [
  z.object({
    operation: MetricAdjustmentsSchema,
    useReferenceFilter: z.literal(true),
    referenceFilters: z.array(FilterConfigSchema).min(1, "At least one reference filter is required"),
  }),
  z.object({
    operation: MetricAdjustmentsSchema,
    useReferenceFilter: z.literal(false),
    adjustmentValue: z.number().min(1, "Adjustment value must be greater than 0"),
  }),
]);

// Metric Filter Schema (for output) - using discriminated union for type safety
export const MetricFilterSchema = z.discriminatedUnion('operator', [
  z.object({
    operator: z.literal(MetricFilterOperatorSchema.enum['between']),
    minValue: z.number(),
    maxValue: z.number(),
  }),
  z.object({
    operator: z.literal(MetricFilterOperatorSchema.enum['ranked_in_top']),
    rankLimit: z.number().min(1, "Rank limit must be greater or equal to 1"),
  }),
  z.object({
    operator: z.literal(MetricFilterOperatorSchema.enum['not_ranked_in_top']),
    rankLimit: z.number().min(1, "Rank limit must be greater or equal to 1"),
  }),
  z.object({
    operator: z.literal(MetricFilterOperatorSchema.enum['>']),
    value: z.number(),
  }),
  z.object({
    operator: z.literal(MetricFilterOperatorSchema.enum['>=']),
    value: z.number(),
  }),
  z.object({
    operator: z.literal(MetricFilterOperatorSchema.enum['<']),
    value: z.number(),
  }),
  z.object({
    operator: z.literal(MetricFilterOperatorSchema.enum['<=']),
    value: z.number(),
  }),
  z.object({
    operator: z.literal(MetricFilterOperatorSchema.enum['=']),
    value: z.number(),
  }),
]);

// Rule Block Config Schema (for output)
export const RuleBlockConfigSchema = z.object({
  metric: MetricSchema,
  aggregation: AggregationsSchema,
  timeConfiguration: TimeConfigurationSchema,
  categoricalfilters: FilterConfigArraySchema,
  metricAdjustment: MetricAdjustmentSchema.optional(),
  metricFilter: MetricFilterSchema.optional(),
})
  .refine((data) => {
    if (data.timeConfiguration.mode === TimeRangeModesSchema.enum['For the First Time']) {
      return data.metricFilter !== undefined;
    }
    return true;
  }, {
    message: "Metric filter is required when time configuration mode is 'For the First Time'",
    path: ["metricFilter"],
  });

// Threshold Schema (used in operators)
export const ThresholdSchema = z.object({
  value: z.number().min(0, "Value must be greater or equal to 0"),
  unit: ThresholdUnitSchema,
});

// Operator Config Schema (for output)
export const OperatorConfigOutputSchema = z.object({
  type: OperatorTypeSchema,
  threshold: ThresholdSchema.optional(),
})
  .refine((data) => {
    if (data.type === OperatorTypeSchema.enum['greater than'] || data.type === OperatorTypeSchema.enum['less than']) {
      return data.threshold !== undefined;
    }
    return true;
  }, {
    message: "Threshold is required when operator type is 'greater than' or 'less than'",
    path: ["threshold"],
  });

// Basic Info Schema (for output)
export const BasicInfoSchema = z.object({
  selectedProduct: SelectedProductSchema,
  beaconClientId: z.string().optional(), // this must be injected by the API layer
  granularity: z.array(ResponseTypesSchema).min(1, "At least 1 response type is required"),
  frequency: FrequencySchema
});

// Rule Group Config Schema (for output)
export const RuleGroupConfigSchema = z.object({
  ruleBlocks: z.array(RuleBlockConfigSchema).min(1, "At least one rule block is required"),
  operators: z.array(OperatorConfigOutputSchema)
})
  .refine((data) => {
    // Check if operators exist and validate no mixing of 'AND' and 'OR'
    if (data.operators.length === 0) {
      return true;
    }

    const hasAnd = data.operators.some(op => op.type === OperatorTypeSchema.enum['AND']);
    const hasOr = data.operators.some(op => op.type === OperatorTypeSchema.enum['OR']);

    // Both 'AND' and 'OR' cannot exist at the same time
    return !(hasAnd && hasOr);
  }, {
    message: "Operators within a rule group cannot contain both 'AND' and 'OR' at the same time",
  });

// Rules Schema (for output)
export const RulesSchema = z.object({
  ruleGroups: z.array(RuleGroupConfigSchema).min(1, "At least one rule group is required"),
  groupOperators: z.array(OperatorConfigOutputSchema)
})
  .refine((data) => {
    // Check if groupOperators exist and have valid types
    if (data.groupOperators.length === 0) {
      return true;
    }

    for (const operator of data.groupOperators) {
      if (operator.type !== OperatorTypeSchema.enum['AND'] && operator.type !== OperatorTypeSchema.enum['OR']) {
        return false;
      }
    }

    const firstOperatorType = data.groupOperators[0].type;
    for (const operator of data.groupOperators) {
      if (operator.type !== firstOperatorType) {
        return false;
      }
    }

    return true;
  }, {
    message: "All group operators must be either 'AND' or 'OR', and they must all have the same type",
  });

// Main Rule JSON Output Schema
export const RuleJsonOutputSchema = z.object({
  basicInfo: BasicInfoSchema,
  globalFilters: z.array(GlobalFilterTypeSchema).min(2, "At least two default global filter are required"),
  rules: RulesSchema,
  alertId: z.string().optional(),
})
  .refine((data) => {
    const selectedProduct = data.basicInfo.selectedProduct;

    if (selectedProduct === 'atlas') {
      const hasCategoryFilter = data.globalFilters.some(filter =>
        filter.type === AtlasFilterFieldTypeSchema.enum['Category']
      );
      const hasRetailerFilter = data.globalFilters.some(filter =>
        filter.type === AtlasFilterFieldTypeSchema.enum['Retailer']
      );
      return hasCategoryFilter && hasRetailerFilter;
    } else if (selectedProduct === 'beacon') {
      const hasBrandFilter = data.globalFilters.some(filter =>
        filter.type === BeaconFilterFieldTypeSchema.enum['Brand']
      );
      const hasRetailerFilter = data.globalFilters.some(filter =>
        filter.type === BeaconFilterFieldTypeSchema.enum['Retailer']
      );
      return hasBrandFilter && hasRetailerFilter;
    }
    return true;
  }, {
    message: "Atlas products must have at least one Category filter and one Retailer filter. Beacon products must have at least one Brand filter and one Retailer filter.",
    path: ["globalFilters"],
  })
  .refine((data) => {
    // Only validate filter type compatibility, let individual schemas handle validation errors
    const selectedProduct = data.basicInfo.selectedProduct;

    if (selectedProduct === 'atlas') {
      return data.rules.ruleGroups.every(ruleGroup =>
        ruleGroup.ruleBlocks.every(ruleBlock => {
          const filters = ruleBlock.categoricalfilters as any[];
          return filters.every((filter: any) => {
            // Only check type compatibility, not full validation
            return !filter.type || AtlasFilterFieldTypeSchema.options.includes(filter.type);
          });
        })
      );
    } else if (selectedProduct === 'beacon') {
      return data.rules.ruleGroups.every(ruleGroup =>
        ruleGroup.ruleBlocks.every(ruleBlock => {
          const filters = ruleBlock.categoricalfilters as any[];
          return filters.every((filter: any) => {
            // Only check type compatibility, not full validation
            return !filter.type || BeaconFilterFieldTypeSchema.options.includes(filter.type);
          });
        })
      );
    }

    return true;
  }, {
    message: "Categorical filter types must be compatible with the selected product",
    path: ["rules"],
  })
  .refine((data) => {
    // Only validate metric compatibility, let individual schemas handle validation errors
    const selectedProduct = data.basicInfo.selectedProduct;

    return data.rules.ruleGroups.every(ruleGroup =>
      ruleGroup.ruleBlocks.every(ruleBlock => {
        if (!ruleBlock.metric) return true; // Let required field validation handle this

        if (selectedProduct === 'atlas') {
          return AtlasMetricsSchema.safeParse(ruleBlock.metric).success;
        } else if (selectedProduct === 'beacon') {
          return BeaconMetricsSchema.safeParse(ruleBlock.metric).success;
        }
        return true;
      })
    );
  }, {
    message: "Metrics must be compatible with the selected product type",
    path: ["rules"],
  })
  .refine((data) => {
    // When any rule block uses "For the First Time" mode, granularity should not contain "WeekId"
    const hasForTheFirstTimeMode = data.rules.ruleGroups.some(ruleGroup =>
      ruleGroup.ruleBlocks.some(ruleBlock =>
        ruleBlock.timeConfiguration.mode === TimeRangeModesSchema.enum['For the First Time']
      )
    );

    if (hasForTheFirstTimeMode) {
      return !data.basicInfo.granularity.includes(ResponseTypesSchema.enum['WeekId']);
    }
    return true;
  }, {
    message: "When using 'For the First Time' time configuration mode, granularity cannot include 'Week Id' response type",
    path: ["basicInfo", "granularity"],
  })
  .refine((data) => {
    // When granularity includes 'Searchterm', all metrics must be keyword sensible
    const hasSearchtermGranularity = data.basicInfo.granularity.includes(ResponseTypesSchema.enum['Searchterm']);
    
    if (hasSearchtermGranularity) {
      return data.rules.ruleGroups.every(ruleGroup =>
        ruleGroup.ruleBlocks.every(ruleBlock => {
          if (!ruleBlock.metric) return true;
          
          return KeywordSensibleMetricsSchema.safeParse(ruleBlock.metric).success;
        })
      );
    }
    return true;
  }, {
    message: "When granularity includes 'Searchterm', all metrics must be keyword sensible. Allowed metrics: Organic Traffic, Paid Traffic, Impressions, Ad Spend, Ad Sales, Ad Units Sold, CTR, CPC, CPA, ROAS",
    path: ["rules"],
  });

// Base Alert Config Schema (common fields for both create and edit)
export const CreateAlertConfigSchema = z.object({
  alertName: z.string().min(1, "Alert name is required"),
  alertDescription: z.string(),
  queryJSON: RuleJsonOutputSchema,
  notifications: NotificationConfigSchema,
});
